import EditIcon from '@mui/icons-material/Edit';
import { Box, Card, useTheme, Button, Badge, Theme, useMediaQuery } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton, Typography, Tooltip, AutoComplete, useWindowSize } from '@utilisourcepackagelibdev/utilisourcepackagelib';
import { MRT_ShowHideColumnsButton, MRT_ToggleFullScreenButton, MRT_ToggleFiltersButton, useMaterialReactTable, MRT_ToggleDensePaddingButton } from 'material-react-table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useNavigate, useSearch } from '@tanstack/react-router';
import useColumns from '@/hooks/useColumns';
import { ticketListTableColumns } from '@/components/Organisms/TicketListTable/ticketListTableColumns';
import useTicketTableStore, { getTableColumnSortingState } from './useTicketTableStore.hook';
import { useDebounce } from '@/hooks/useDebounce';
import useSettings, { createUserSettingsOptions } from '@/services/setting/setting.options';
import { createUserQueryOptions } from '@/services/auth/auth.options';
import { FilterToggleButton } from './TicketList.page';
import { queryClient } from '@/lib/queryClient';
import { createAgentsByCustomerQueryOptions, createAllTicketsQueryOptions, createCustomersQueryOptions } from '@/services/ticket/ticket.options';
import { FormProvider, useWatch, useForm } from 'react-hook-form';
import Permissions from '@/permissions/Permissions';
import Grid from '@mui/material/Grid2';
import useTicketList from '@/contexts/stores/ticketList.store';
import { zodResolver } from '@hookform/resolvers/zod';
import { ticketListFilterSchema } from '@/interfaces/schemas/ticketListFilter.schema';
import { TicketListFilterSchema } from '@/interfaces/schemas/ticketListFilter.schema';
import { MapIcon } from 'lucide-react';

type CategoryLabel = 'Active' | 'Closed' | 'All' | 'Assigned';

const useTicketTable = () => {

  const height = useWindowSize().height
  const { drawer } = useSearch({ from: '/_auth/' });
  const { ticketListState, updateTicketList } = useTicketList();
  const { setCustomFilters } = useTicketTableStore()

  const [isOpen, setIsOpen] = useState(false)
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const handleClose = () => {
    // Wait for animation to complete before navigating
    setTimeout(() => {
      navigate({ to: '/', search: { drawer: undefined } });
    }, 300) // Match this with MUI's transition duration
  }

  const handleCloseDrawer = () => {
    handleClose()
    navigate({ to: '/', search: { drawer: undefined } });
  };

  const customers = useQuery(createCustomersQueryOptions())


  const defaultValues: TicketListFilterSchema = {
    customer_filter: null,
    category_filter: null,
  };

  const methods = useForm<TicketListFilterSchema>({
    mode: 'all',
    resolver: zodResolver(ticketListFilterSchema),
    defaultValues,
  });


  const customerValue = useWatch({ control: methods.control, name: 'customer_filter' });
  const categoryValue = useWatch({ control: methods.control, name: 'category_filter' });


  const handleSubmit = (formValues: any) => {
    ticketListTable.resetColumnFilters();
    updateTicketList({ selectedCustomer: customerValue, selectedCategory: categoryValue });
    setCustomFilters({ customer: formValues.customer_filter, category: formValues.category_filter });
    // ticketListTable.setCreatingRow(formValues)
    const customer = ticketListTable.setColumnFilters((prev) => [
      ...prev,
      {
        id: 'CustomerName',
        value: formValues.customer_filter ? formValues.customer_filter?.label : '',
      },
    ]);

    return { customer };
  };

  // const handleCustomerChange = (customer: any) => {
  //   const customerId = customer ? customer.id : undefined;
  //   // setSelectedCustomer(customerId);
  //   updateTicketList({ selectedCustomer: customerId })
  // };

  // const handleCategoryChange = (category: any) => {
  //   const categoryValue = category ? category.label : undefined;
  //   // setSelectedCategory(categoryValue);
  //   updateTicketList({ selectedCategory: categoryValue })
  // };

  const handleToggleTicketListMap = () => {
    updateTicketList({ ticketListMapOpen: true });
  };

  const handleToggleAssigneeFilter = () => {
    const currentFilterApplied = ticketListState.assigneeFilterApplied;

    updateTicketList({ assigneeFilterApplied: !currentFilterApplied });

    if (!currentFilterApplied) {
      const assignee = ticketListTable.setColumnFilters((prev) => [
        ...prev,
        {
          id: 'Assignee',
          value: [agentsByCustomer.data?.ContextUser?.Name],
        },
      ]);

      return { assignee };
    } else {
      ticketListTable.setColumnFilters((prev) => prev.filter((filter) => filter.id !== 'Assignee'));
    }
  };

  // const { ticketListTable } = useTicketListTable({
  //   // setEditRow,
  //   // setEditRowValues,
  //   // setCustomerDrawerOpen,
  //   sorting,
  //   setSorting,
  //   loading: allTickets.isLoading,
  //   data: allTickets.data ? allTickets.data : [],
  //   refreshQueryKey: ['all-tickets', ticketListState?.selectedCustomer?.id, ticketListState?.selectedCategory?.label],
  // });

  // useEffect(() => {
  //   updateTicketList({ selectedCustomer: customerValue, selectedCategory: categoryValue })
  //   // updateTicketList({ selectedCategory: categoryValue })
  // }, [customerValue, categoryValue])

  const baseCategories = [
    { id: 0, label: 'Active' },
    { id: 1, label: 'Closed' },
    { id: 2, label: 'All' },
  ];

  // Get categories based on customer selection
  const getCategories = () => {
    // Check if the selected customer is Gateway
    const isGatewayCustomer = customerValue?.id === 9;

    return isGatewayCustomer
      ? [...baseCategories, { id: 3, label: 'Assigned' }]
      : baseCategories;
  };

  // useEffect(() => {
  //   // When the component mounts, check if the assignee filter should be applied
  //   if (ticketListState.assigneeFilterApplied) {
  //     ticketListTable.setColumnFilters((prev) => [
  //       ...prev,
  //       { id: 'Assignee', value: ['Arna Wade'] }, //! Replace this with a dynamic agent name
  //     ]);
  //   } else {
  //     ticketListTable.setColumnFilters((prev) => prev.filter((filter) => filter.id !== 'Assignee'));
  //   }
  // }, [ticketListState.assigneeFilterApplied, ticketListTable));

  const onSubmit = (formValues: any) => {
    console.log('hello world submit')
    console.log(formValues);
  }

  const initialCustomer = {
    id: customers.data?.[0].CustomerEntId,
    label: customers.data?.[0].EntName,
  }

  useEffect(() => {
    if (customers?.data) {
      async function initialize() {
        console.log('initial customer', initialCustomer)
        methods.setValue('customer_filter', ticketTableState?.customFilters?.customer ?? initialCustomer);
        methods.setValue('category_filter', ticketTableState?.customFilters?.category?.label ? { id: ticketTableState?.customFilters?.category?.id, label: ticketTableState?.customFilters?.category?.label } : { id: 0, label: 'Active' });
        await initializeStore();
        await reset();
        updateTicketList({ selectedTicket: { id: 0 } });
        const formValues = methods.getValues();
        handleSubmit(formValues);
      }
      initialize();
    }
  }, [customers?.data]);

  useEffect(() => {
    if (categoryValue?.label === 'Assigned' && customerValue?.id !== 9) {
      methods.setValue('category_filter', null);
    }

    ticketListTable.resetColumnFilters();
    updateTicketList({ selectedCustomer: customerValue, selectedCategory: categoryValue });
    setCustomFilters({
      customer: customerValue ?? undefined,
      category: categoryValue ? {
        id: categoryValue.id,
        label: categoryValue.label as CategoryLabel
      } : undefined
    });
    ticketListTable.setColumnFilters((prev) => [
      ...prev,
      {
        id: 'CustomerName',
        value: customerValue?.label,
      }
    ]);
  }, [customerValue?.id, categoryValue?.label])


  const {
    initializeStore,
    reset,
    ticketTableState,
    setPagination,
    setSorting,
    setColumnFilters,
    setColumnVisibility,
    setDensity,
    setGlobalFilter,
    setShowColumnFilters,
    setTicketIds,
    setColumnOrder,
  } = useTicketTableStore();

  const columns = useColumns(ticketListTableColumns, ticketTableState?.customFilters?.customer?.id);

  const theme = useTheme();
  const navigate = useNavigate()

  const allTickets = useQuery(createAllTicketsQueryOptions(ticketTableState?.customFilters?.customer?.id, ticketTableState?.customFilters.category?.label))
  const agentsByCustomer = useQuery(createAgentsByCustomerQueryOptions(ticketTableState?.customFilters?.customer?.id))

  const data = allTickets?.data || [];
  const refreshQueryKey = ['all-tickets', ticketTableState?.customFilters?.customer?.id, ticketTableState?.customFilters.category?.label]

  const { updateSettings } = useSettings(agentsByCustomer?.data?.ContextUser?.AgentId);

  const Message = (message: string) => {
    return <Typography>{message}</Typography>;
  };

  const handleRefresh = async () => {
    // console.log('refreshing')
    const previousData = queryClient.getQueryData(refreshQueryKey);
    // console.log(previousData)

    allTickets.refetch()

    await queryClient.invalidateQueries({ queryKey: refreshQueryKey, exact: true }); // Replace 'yourQueryKey' with the actual query key
    const newData = queryClient.getQueryData(refreshQueryKey);
    // console.log(newData)
    if (newData) {
      if (JSON.stringify(newData) !== JSON.stringify(previousData)) {
        enqueueSnackbar(Message('New records found!'), { variant: 'success' });
      } else {
        enqueueSnackbar(Message('Table successfully updated'), {
          variant: 'default',
          style: { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText },
        });
      }
      // } else {
      // enqueueSnackbar(Message('No new records found'), { variant: 'error' })
    }
  };

  // Memoize the update function
  const debouncedUpdate = useDebounce(() => {
    if (ticketListTable) {
      // console.log('...updating');
      updateTicketIds();
    }
  }, 500);

  const updateTicketIds = () => {
    const newTicketIds = ticketListTable?.getPrePaginationRowModel()?.rows
      .map(row => row.original?.TicketExt?.Ticket?.TId)
      .filter(Boolean);

    setTicketIds(newTicketIds);
  };

  const saveTicketTableColumnOrder = async (newColumnOrder: any) => {

    const agentByContext = await queryClient.fetchQuery(createUserQueryOptions({ ticketid: 0, customerid: 0, limittocustomer: false }));
    const settings = await queryClient.fetchQuery(createUserSettingsOptions(agentByContext?.ContextUser?.AgentId));

    const previousData = settings?.data || {};
    let preparedData = {}
    if (!previousData || !previousData.Data || previousData.Data.length === 0) {
      preparedData = {
        Id: agentByContext?.ContextUser?.AgentId,
        Data: [
          {
            Identifier: "ticketTableColumnSorting",
            ColumnOrder: newColumnOrder,
          },
        ],
      };
    } else {
      const updatedData = [...previousData.Data];

      const columnSortingIndex = updatedData.findIndex(
        (entry: any) => entry.Identifier === "ticketTableColumnSorting"
      );

      if (columnSortingIndex !== -1) {
        updatedData[columnSortingIndex] = {
          ...updatedData[columnSortingIndex],
          ColumnOrder: newColumnOrder,
        };
      } else {
        updatedData.push({
          Identifier: "ticketTableColumnSorting",
          ColumnOrder: newColumnOrder,
        });
      }

      preparedData = {
        SettingsId: previousData.SettingsId,
        Type: previousData.Type,
        Id: previousData.Id,
        Data: updatedData,
      };
    }

    try {
      await updateSettings({
        actionType: "updateTicketTableColumns",
        data: preparedData,
      });
    } catch (error) {
      console.error("Error saving column order:", error);
    }
  };


  // useEffect(() => {
  //   initializeStore();
  //   reset();
  // }, []);

  // useEffect(() => {
  //   const getColumnOrder = async () => {
  //     const columnSortingState = await getTableColumnSortingState()
  //     setColumnOrder(columnSortingState)
  //   }

  //   getColumnOrder();
  // }, []);

  useEffect(() => {
    setColumnVisibility(prev => ({
      ...prev,
      conditionalColumn: ticketTableState?.customFilters?.customer?.id === 6
    }));
  }, [ticketTableState?.customFilters?.customer?.id]);

  useEffect(() => {
    if (ticketTableState?.customFilters?.customer?.id && ticketTableState?.customFilters?.customer?.id > 0) {
      debouncedUpdate();
    }
  }, [
    ticketTableState.sorting,
    ticketTableState.columnFilters,
    ticketTableState.globalFilter,
    ticketTableState?.customFilters?.customer?.id,
    ticketTableState?.customFilters?.category?.label,
    data // Add this to update when data changes
  ]);

  const ticketListTable = useMaterialReactTable({
    data,
    columns,
    muiTableBodyCellProps: {
      sx: {
        p: '4px 4px',
      }
    },
    enableSorting: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnFilters: true,
    enableGlobalFilter: false,
    positionGlobalFilter: 'none',
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onPaginationChange: setPagination,
    enableTopToolbar: true,
    positionToolbarAlertBanner: 'bottom',
    enableColumnOrdering: true,
    onColumnOrderChange: (newColumnOrder) => {
      saveTicketTableColumnOrder(newColumnOrder);
      setColumnOrder(newColumnOrder);
    },
    enableFilterMatchHighlighting: false,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '', //change header text
        size: 10,
      },
    },
    muiTablePaperProps: {
      sx: {
        p: 2,
        borderRadius: '6px',
        boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px 2px',
        backgroundImage: 'none',
        height: `${height - 120}px`, // Account for page padding
        display: 'flex',
        flexDirection: 'column',
        // height: height - 100,
      },
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        zIndex: 1,
      }
    },
    muiTableBodyProps: {
      sx: {
        border: 'none',
        '& tr:nth-of-type(odd) > td': {
          backgroundColor: theme.palette.background.default,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        flex: 1, // This will make the table container fill available space
        height: 'auto', // Let it adjust to content
      }
    },
    getRowId: (row: any) => row?.name,
    renderRowActions: ({ row, table }: any) => (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
        onClick={(event: any) => {
          event.stopPropagation();
        }}
      >
        <Tooltip title="Edit">
          <IconButton
            id="edit"
            size="small"
            onClick={(event: any) => {
              event.stopPropagation();
              navigate({
                to: `/ticket/$ticketId`,
                params: { ticketId: row.original.TicketExt.Ticket.TId },
                search: { variant: 'edit', step: 0 }
              })
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableBodyRowProps: ({ row }: any) => ({
      onClick: (event: any) => {
        event.stopPropagation();
        navigate({
          to: `/ticket/$ticketId`,
          params: { ticketId: row.original.TicketExt.Ticket.TId },
          search: { variant: 'edit', step: 0 }
        })
      },
      sx: {
        cursor: 'pointer', //you might want to change the cursor too when adding an onClick
        height: '10px',
      },
    }),
    renderTopToolbar: ({ table }: any) => (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <Grid container sx={{ height: '100%', width: '100%' }}>
            {/* <Card sx={{ p: 4, width: '100%' }} > */}
            {/* <Typography variant="h4" sx={{ mb: 4 }}>Filters</Typography> */}
            <Grid
              size={12}
              sx={{ p: 2 }}
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            // height='100px'
            // rowGap={4}
            // paddingX={2}
            // paddingY={1}
            >
              {/* <FilterToggleButton /> */}
              <Grid
                // size={9}
                display="flex"
                flexDirection="row"
                alignItems="center"
                flexWrap="wrap"
              >

                <Grid size={3} sx={{ mr: 4, minWidth: "200px", paddingBottom: '4px', paddingTop: '4px' }}>

                  <AutoComplete
                    id="customer_filter"
                    options={
                      customers.isPending
                        ? []
                        : customers?.data?.map((customer: any) => ({
                          id: customer.CustomerEntId,
                          label: customer.EntName,
                        })) || []
                    }
                    loading={customers.isPending}
                    autocompleteProps={{
                      id: 'customer_filter',
                      loadingText: 'Loading Customers...',
                      size: 'small',
                    }}
                    multiple={false}
                    label="Customer"
                  />
                </Grid>
                <Grid
                  size={2}
                  sx={{ mr: 4, minWidth: "140px", paddingBottom: '4px', paddingTop: '4px' }}
                >

                  <AutoComplete
                    id="category_filter"
                    options={getCategories()}
                    label="Category"
                    autocompleteProps={{
                      id: 'category_filter',
                      size: 'small',
                    }}

                  />
                </Grid>
                <Grid>

                  <div style={{ display: 'flex', justifyContent: 'end', paddingBottom: '4px', paddingTop: '4px' }}>
                    <Button
                      id="clear"
                      size='small'
                      variant="outlined"
                      sx={{ marginRight: 2 }}
                      onClick={() => {
                        methods.reset();
                        ticketListTable.resetColumnFilters();
                        setCustomFilters({ customer: undefined, category: undefined });
                      }}
                    >
                      Clear
                    </Button>
                    <Button id="filterResults" variant="contained" type="submit" size='small' sx={{ minWidth: "96px" }}>
                      Filter Results
                    </Button>
                  </div>
                </Grid>
              </Grid>
              {/* <Grid> */}
              <Grid
                display="flex"
                flexDirection="row"
                alignItems="center"
                sx={{ paddingBottom: '4px', paddingTop: '4px' }}
              >

                {/* <Button
                  size='small'
                  id="ticketListMap"
                  variant="contained"
                  onClick={handleToggleTicketListMap}
                  sx={{ mr: 5 }}
                  disabled={!ticketListTable.getRowModel().rows.length}
                >
                  Map
                </Button> */}
                <IconButton
                  size='small'
                  id="ticketListMap"
                  //  variant="contained"
                  onClick={handleToggleTicketListMap}
                  sx={{
                    mr: 5,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.contrastText,
                    }
                  }}
                  disabled={!ticketListTable.getRowModel().rows.length}
                >
                  <MapIcon />
                </IconButton>

                <>
                  <Button
                    size='small'
                    id="assignedToMe"
                    variant={ticketTableState.columnFilters.some(filter => filter.id === 'Assignee') ? 'outlined' : 'contained'}
                    onClick={handleToggleAssigneeFilter}
                    sx={{ minWidth: "110px" }}
                  >
                    Assigned to Me
                  </Button>
                  <Badge
                    badgeContent={ticketTableState.columnFilters.some(filter => filter.id === 'Assignee') ? 'On' : 'Off'}
                    color="secondary"
                    sx={{ ml: 2, mb: 5, mr: 5 }}
                  />
                </>

                <Permissions target={'createTicket'} disableMode>
                  <Button
                    size='small'
                    id="createTicket"
                    variant='contained'
                    onMouseDown={() => {
                      console.log('open drawer');
                      navigate({
                        to: '/',
                        search: (prev: any) => ({
                          ...prev,
                          drawer: 'create'
                        })
                      })
                    }}
                    sx={{ minWidth: "110px" }}
                  >
                    + Create Ticket
                  </Button>
                </Permissions>

              </Grid>
              {/* </Grid> */}
            </Grid>

            {/* </Card> */}
            <Grid size={12} display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end" flexWrap="wrap">

              <FilterToggleButton />
              <MRT_ToggleFiltersButton table={ticketListTable} />
              <IconButton onClick={handleRefresh} id={'refresh'}>
                <RefreshIcon />
              </IconButton>
              <MRT_ShowHideColumnsButton table={ticketListTable} />
              <MRT_ToggleFullScreenButton table={ticketListTable} />
            </Grid>
          </Grid>
        </form>
      </FormProvider >
    ),
    // renderToolbarInternalActions: ({ table }: any) => (
    //   <>
    //     <FilterToggleButton />
    //     <MRT_ToggleFiltersButton table={ticketListTable} />
    //     <IconButton onClick={handleRefresh} id={'refresh'}>
    //       <RefreshIcon />
    //     </IconButton>
    //     <MRT_ShowHideColumnsButton table={ticketListTable} />
    //     <MRT_ToggleFullScreenButton table={ticketListTable} />
    //   </>
    // ),
    initialState: {
      showGlobalFilter: false,
      density: 'compact',
      sorting: ticketTableState.sorting,
      columnVisibility: ticketTableState.columnVisibility,
      columnFilters: ticketTableState.columnFilters,
      // globalFilter: ticketTableState.globalFilter,
      showColumnFilters: ticketTableState.showColumnFilters,
      pagination: ticketTableState.pagination,
      columnOrder: ticketTableState.columnOrder,
    },
    state: {
      showAlertBanner: allTickets.isError,
      showSkeletons: allTickets.isLoading,
      showProgressBars: allTickets.isFetching,
      sorting: ticketTableState.sorting,
      columnVisibility: ticketTableState.columnVisibility,
      columnFilters: ticketTableState.columnFilters,
      // globalFilter: ticketTableState.globalFilter,
      showColumnFilters: ticketTableState.showColumnFilters,
      pagination: ticketTableState.pagination,
      columnOrder: ticketTableState.columnOrder
    },
  });

  return {
    ticketListTable,
    refetch: allTickets.refetch,
  };
}

export default useTicketTable